import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { Loader2, Shield } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MinimalFooter from '@/components/common/MinimalFooter';
import MinimalHeader from '@/components/common/MinimalHeader';
import type { InsuranceType } from '@/context/InsuranceTypeContext';
import { useAdvisor } from '@/context/useAdvisorContext';
import { useInsuranceType } from '@/hooks/useInsuranceType';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { getAdvisorPublicInfo } from '@/lib/auth/profile-service';
import { toast } from '@/lib/toast';

const VALID_TYPES: InsuranceType[] = ['auto', 'home', 'life', 'disability'];
const ACTIVE_TYPES: InsuranceType[] = ['auto', 'home'];

export const Route = createFileRoute('/_advisor/a/$username/$type')({
  component: AdvisorSpecificFormPage,
});

function AdvisorSpecificFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { username, type } = useParams({
    from: '/_advisor/a/$username/$type',
  });
  const { setAdvisor } = useAdvisor();
  const { setSelectedType } = useInsuranceType();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAdvisorAndRedirect() {
      // Check if type is valid
      if (!VALID_TYPES.includes(type as InsuranceType)) {
        navigate({
          to: '/a/$username',
          params: { username },
          replace: true,
        });
        return;
      }

      // Check if type is active (life/disability are coming soon)
      if (!ACTIVE_TYPES.includes(type as InsuranceType)) {
        toast.info('toast.comingSoon', {
          messageParams: { service: t(`home.services.${type}.title`) },
        });
        navigate({
          to: '/a/$username',
          params: { username },
          replace: true,
        });
        return;
      }

      setError(null);

      const result = await getAdvisorPublicInfo(username);

      if (result.success && result.data) {
        setAdvisor(result.data);
        setSelectedType(type as InsuranceType);
        // Redirect to consent page with advisor context
        navigate({
          to: '/consent',
          state: { advisorUsername: username } as Record<string, unknown>,
          replace: true,
        });
      } else {
        setError(t('advisor.notFound'));
      }
    }

    fetchAdvisorAndRedirect();
  }, [username, type, setAdvisor, setSelectedType, navigate, t]);

  const handleReturnHome = () => {
    navigate({ to: '/' });
  };

  if (error) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-primary">
        <MinimalHeader showLanguageSwitcher={true} />
        <main className="flex flex-1 items-center justify-center px-4">
          <div className="flex flex-col items-center gap-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <Shield className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-foreground-primary">
              {t('advisor.notFound')}
            </h1>
            <p className="max-w-md text-foreground-secondary">
              {t('advisor.notFoundDescription')}
            </p>
            <button
              onClick={handleReturnHome}
              className="rounded-lg bg-accent-primary px-6 py-3 font-medium text-white transition-colors hover:bg-accent-hover"
            >
              {t('advisor.returnHome')}
            </button>
          </div>
        </main>
        <MinimalFooter variant={isDesktop ? 'desktop' : 'mobile'} />
      </div>
    );
  }

  // Loading state while fetching advisor and redirecting
  return (
    <div className="flex min-h-screen flex-col bg-surface-primary">
      <MinimalHeader showLanguageSwitcher={true} />
      <main className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-accent-primary" />
          <p className="text-foreground-secondary">{t('advisor.loading')}</p>
        </div>
      </main>
      <MinimalFooter variant={isDesktop ? 'desktop' : 'mobile'} />
    </div>
  );
}
